import type React from 'react';
import { useEffect, useState } from 'react';
import { resolveAppBaseUrlFromHostname } from '../utils/structuredAiEnvironmentUrls';
// Center illustration will be inlined directly below; keep logo import bundled
import Logo from '../assets/logo.svg';

type AuthFailedProps = {
  onLogin?: () => void;
  loginHref?: string;
  className?: string;
};

/**
 * AuthFailed
 * Figma-accurate session/auth expired screen composed as a reusable component.
 * Uses Tailwind utility classes with minimal inline styles for precise typography/spacing.
 */
const AuthFailed: React.FC<AuthFailedProps> = ({
  onLogin,
  loginHref = '',
  className,
}) => {
  // Use state to ensure redirect URL is determined on client side only (avoiding SSR hydration mismatch)
  const [redirectHref, setRedirectHref] = useState<string>('');

  useEffect(() => {
    // Determine environment-specific redirect URL on client side only
    const envHref = resolveAppBaseUrlFromHostname(
      window.location.hostname.toLowerCase(),
    );

    const finalHref = loginHref && loginHref !== '/' ? loginHref : envHref;
    setRedirectHref(finalHref);
  }, [loginHref]);

  return (
    <div
      className={[
        'w-full min-h-[100vh] flex flex-col bg-white',
        className || '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {/* Header strip */}
      <header className="w-full bg-white border-b border-black/5">
        <div className="mx-auto w-full px-6 sm:px-10 py-4 flex items-center">
          <img
            src={Logo}
            alt="Logo"
            width={194}
            height={52}
            className="h-[52px] w-auto"
          />
        </div>
      </header>

      {/* Main content */}
      <main className="flex-1 w-full px-6 sm:px-10 flex items-center justify-center">
        <div className="w-full max-w-[625px] flex flex-col items-center gap-6 p-0">
          {/* Illustration card */}
          <div className="w-full flex justify-center">
            <div
              className="bg-white rounded-2xl p-12 flex flex-col items-center"
              role="img"
              aria-label="Session expired illustration"
            >
              {/* Inline SVG to avoid any routing/asset-path issues */}
              <div className="w-[207px] h-[214px] flex items-center justify-center">
                <svg
                  width="207"
                  height="214"
                  viewBox="0 0 207 214"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  aria-hidden="true"
                  focusable="false"
                >
                  <path
                    d="M180.368 26.4041C206.576 46.8651 211.057 85.6347 204.019 118.083C197.678 147.319 171.783 164.954 146.746 181.413C120.276 198.814 91.8118 221.298 61.6051 211.704C30.6788 201.881 16.3709 168.107 7.13145 137.069C-1.54662 107.917 -5.31018 74.4151 13.6467 50.6004C31.2689 28.4623 63.871 34.6306 91.9135 30.5608C122.645 26.1007 155.912 7.31082 180.368 26.4041Z"
                    fill="#EDEDED"
                  />
                  <mask
                    id="mask0_1_12"
                    maskUnits="userSpaceOnUse"
                    x="10"
                    y="0"
                    width="173"
                    height="202"
                  >
                    <path d="M182.711 0H10V201.952H182.711V0Z" fill="white" />
                  </mask>
                  <g mask="url(#mask0_1_12)">
                    <path
                      d="M161.927 69.3712C163.062 72.8882 165.059 76.1263 167.697 78.7247C169.356 80.3592 171.411 82.0379 171.492 84.36C171.526 85.3628 171.166 86.3392 170.712 87.2352C169.347 89.9321 167.13 92.105 165.534 94.6737C163.937 97.2423 163.01 100.602 164.478 103.245C165.574 105.221 167.821 106.448 168.629 108.557C169.543 110.941 168.274 113.648 166.5 115.49C164.726 117.333 162.461 118.654 160.699 120.508C158.937 122.362 157.704 125.094 158.665 127.46C159.246 128.89 160.551 129.985 160.918 131.484C161.328 133.154 160.478 134.847 159.654 136.357C157.74 139.87 155.825 143.382 153.911 146.895C147.005 148.355 139.921 148.972 132.866 148.727C129.678 148.617 126.404 148.306 123.562 146.864C121.424 145.779 119.644 144.106 118.05 142.321C111.629 135.134 107.815 125.773 106.953 116.194C106.092 106.616 108.123 96.8556 112.339 88.2021C115.814 81.0679 120.885 74.539 127.646 70.3472C134.693 65.9787 143.317 64.3733 151.561 65.3538C155.923 65.8726 161.294 67.4093 161.927 69.3712H161.927Z"
                      fill="#E3E3E3"
                      stroke="#010101"
                      strokeWidth="0.62243"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M170.875 72.4865C134.467 81.9238 97.9055 90.7733 61.2094 99.0296C55.5596 79.7377 50.2963 60.3155 44.832 40.9495C81.1723 31.0666 117.154 20.8281 153.165 9.64331C159.76 30.4969 166.199 51.3999 170.875 72.4865Z"
                      fill="white"
                      stroke="#010101"
                      strokeWidth="0.62243"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M153.165 9.64365C152.459 6.54569 151.585 3.98086 150.27 0.283691C145.2 1.08565 83.2564 19.7059 41.832 31.3445C42.9778 34.5671 43.1568 37.1572 44.8322 40.9495C80.1955 30.7359 116.163 20.2311 153.165 9.64328V9.64365Z"
                      fill="#010101"
                      stroke="#010101"
                      strokeWidth="0.62243"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M50.9613 63.0291C49.0355 65.3462 47.1094 67.6633 45.1836 69.9805C49.3022 74.6926 53.4318 79.4186 56.9559 84.5864C54.9922 77.7003 53.0289 70.8146 50.9613 63.0294V63.0291Z"
                      fill="white"
                      stroke="#010101"
                      strokeWidth="0.62243"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M79.6838 94.8201C78.2676 101.342 77.2148 108.454 76.5596 115.094C93.5174 115.094 107.969 114.531 124.676 115.198C124.762 104.714 125.775 93.6984 127.686 83.3877C112.293 87.3665 98.1144 90.0556 79.6838 94.8201Z"
                      fill="white"
                      stroke="#010101"
                      strokeWidth="0.62243"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M85.9397 143.098C102.649 142.747 119.337 141.424 135.891 139.137C139.576 138.628 143.56 137.915 146.068 135.18C148.851 132.145 148.896 127.245 146.842 123.681C144.787 120.116 141.012 117.79 137.053 116.625C133.093 115.461 128.911 115.327 124.784 115.234C108.21 114.862 91.6279 115.012 75.0567 115.478C70.6095 115.603 66.1149 115.758 61.8237 116.926C57.5325 118.093 53.4009 120.383 50.856 124.014C48.8851 126.827 47.9822 130.269 47.7185 133.688C47.3072 139.018 48.3581 144.343 49.4838 149.57C52.3997 163.108 55.8556 176.53 59.841 189.795C66.3319 187.985 72.8232 186.175 79.3141 184.365C74.675 170.237 71.2659 155.709 69.1159 141.189C73.3195 141.074 77.5231 140.958 81.727 140.843C83.498 140.794 84.6028 140.799 86.1313 140.613L85.9401 143.098H85.9397Z"
                      fill="#010101"
                      stroke="#010101"
                      strokeWidth="0.62243"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M86.03 125.125C84.2746 125.231 81.6216 125.242 79.8662 125.349C79.5975 125.365 79.2993 125.395 79.1195 125.595C78.4268 126.363 79.1172 129.893 79.1172 131.786C79.8536 131.923 80.5896 132.061 81.326 132.199C80.8636 132.912 79.9473 134.545 79.8559 134.702C79.7436 134.897 80.5441 142.214 80.4284 147.742C83.0113 143.77 85.8862 139.279 86.049 138.908C86.2432 138.465 86.03 130.01 86.03 125.125V125.125Z"
                      fill="white"
                      stroke="#010101"
                      strokeWidth="0.62243"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M76.3391 184.717C76.8741 186.384 77.409 188.051 77.9439 189.718C78.0259 189.973 78.1063 190.26 77.9951 190.494C77.9113 190.67 77.7375 190.774 77.5721 190.869C76.1741 191.67 74.7764 192.47 73.3784 193.271C72.9929 192.633 72.513 191.713 72.1276 191.075C71.7671 191.946 71.5452 193.108 71.3529 193.881C71.3123 194.045 62.9165 198.395 57.9897 200.403C59.2212 197.284 62.9187 192.089 64.1498 188.97C64.2917 188.611 64.4393 188.243 64.703 187.972C65.0236 187.642 65.4762 187.496 65.9113 187.361C68.9464 186.414 71.9815 185.469 76.3384 184.717H76.3391Z"
                      stroke="#010101"
                      strokeWidth="0.62243"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M34.5844 201.434C32.0762 196.264 28.8754 190.962 25.0936 186.627C23.3614 184.641 21.4246 182.704 18.9574 181.763C16.4903 180.821 13.3702 181.124 11.6467 183.117C9.69548 185.373 10.1583 188.796 11.0689 191.632C12.1679 195.055 13.7564 198.322 16.1648 201.471C22.2198 201.809 27.7846 201.657 34.584 201.434L34.5844 201.434Z"
                      fill="#55D087"
                      stroke="#010101"
                      strokeWidth="0.62243"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M130.183 121.67C115.467 122.646 100.704 122.928 85.9611 122.515C86.1701 129.374 86.1629 136.24 85.9395 143.098"
                      stroke="white"
                      strokeWidth="0.62243"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M25.7981 178.435C25.4605 175.769 25.3641 172.95 26.5057 170.515C27.6472 168.08 30.3617 166.193 32.9928 166.797C34.9113 167.237 36.3238 168.824 37.5173 170.382C44.1748 179.075 48.9631 190.575 52.8499 201.09C47.768 201.401 35.1613 201.621 34.5843 201.435C34.008 201.248 26.7291 185.788 25.7981 178.436V178.435Z"
                      fill="#55D087"
                      stroke="#010101"
                      strokeWidth="0.62243"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M154.943 197.714C159.333 179.583 162.937 161.972 167.542 142.699C167.889 141.245 166.776 139.849 165.274 139.852C137.884 139.906 118.096 140.461 92.874 140.504C91.6016 140.507 90.4987 141.38 90.2161 142.615C86.3057 159.679 81.7284 179.679 79.0063 197.568C102.856 197.568 129.097 197.714 154.943 197.714Z"
                      fill="#80B9FF"
                      stroke="#010101"
                      strokeWidth="0.62243"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M31.624 169.78C35.4547 180.351 39.2853 190.923 43.1164 201.494"
                      stroke="#010101"
                      strokeWidth="0.62243"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M15.6636 183.824C18.221 189.833 21.0839 195.713 24.2377 201.434"
                      stroke="#010101"
                      strokeWidth="0.62243"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M130.266 82.7811C131.651 85.5615 133.061 88.3793 135.137 90.6942C137.213 93.0091 140.076 94.7995 143.192 94.9113C147.401 95.0619 151.222 92.0987 153.307 88.4571C155.393 84.8154 156.08 80.5704 156.73 76.4292C149.153 77.6623 141.263 80.385 130.266 82.7814L130.266 82.7811Z"
                      fill="white"
                      stroke="#010101"
                      strokeWidth="0.62243"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M151.481 194.328C155.601 177.81 159.006 161.567 163.423 143.855C136.861 143.855 117.853 144.633 93.5728 144.633C89.9861 159.838 85.5905 178.229 83.0547 194.408C104.386 194.408 128.365 194.328 151.481 194.328Z"
                      fill="white"
                      stroke="#010101"
                      strokeWidth="0.62243"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M164.817 154.885C166.709 155.652 168.061 157.34 169.118 159.08C171.378 162.798 172.692 167.08 172.906 171.421C173.896 171.179 174.885 170.937 175.875 170.696C175.397 167.06 174.459 163.453 172.762 160.198C171.065 156.944 168.576 154.048 165.406 152.179C165.23 153.169 164.997 154.149 164.817 154.885Z"
                      fill="#80B9FF"
                      stroke="#010101"
                      strokeWidth="0.62243"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M175.746 170.853C176.022 172.023 176.216 173.214 176.324 174.411C175.464 174.708 174.553 174.863 173.642 174.865C173.432 173.675 173.223 172.484 173.013 171.294C173.855 170.934 174.756 170.712 175.746 170.853V170.853Z"
                      stroke="#010101"
                      strokeWidth="0.62243"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M179.963 175.35L182.426 176.383C182.348 176.355 182.27 176.328 179.963 175.35Z"
                      stroke="#010101"
                      strokeWidth="0.62243"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M176.467 181.069L177.062 182.886"
                      stroke="#010101"
                      strokeWidth="0.62243"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M171.541 177.442L169.877 179.037"
                      stroke="#010101"
                      strokeWidth="0.62243"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M154.882 197.493C154.826 197.851 154.798 198.166 154.787 198.458C154.721 200.122 153.34 201.434 151.667 201.434C116.23 201.429 85.3374 201.338 49.4779 201.333C48.1645 201.333 47.2134 200.081 47.5742 198.824C47.578 198.811 47.5818 198.798 47.5852 198.785C47.8333 197.921 48.6338 197.333 49.5363 197.358C64.2064 197.747 130.741 197.493 154.882 197.493Z"
                      fill="#80B9FF"
                      stroke="#010101"
                      strokeWidth="0.62243"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M101.189 197.482C100.689 197.572 100.252 197.903 99.9766 198.328C99.7012 198.752 99.5779 199.261 99.5589 199.766C99.5456 200.126 99.5874 200.501 99.7725 200.81C99.9576 201.119 100.311 201.348 100.671 201.303"
                      stroke="#010101"
                      strokeWidth="0.62243"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M142.664 155.939C139.45 158.588 136.282 161.293 133.162 164.051"
                      stroke="#010101"
                      strokeWidth="0.62243"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M133.698 155.608C136.943 158.073 140.07 160.691 143.065 163.451"
                      stroke="#010101"
                      strokeWidth="0.62243"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M110.971 156.792C107.571 159.514 104.092 162.138 100.539 164.659"
                      stroke="#010101"
                      strokeWidth="0.62243"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M100.823 156.651C104.482 159.56 108.36 162.196 112.414 164.53"
                      stroke="#010101"
                      strokeWidth="0.62243"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M109.999 178.353C113.837 175.434 118.733 174.031 123.564 174.075C128.395 174.12 133.155 175.558 137.406 177.843"
                      stroke="#010101"
                      strokeWidth="0.62243"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M134.094 176.295C133.974 177.686 133.706 179.064 133.296 180.4C133.055 181.182 132.753 181.971 132.199 182.574C131.644 183.177 130.784 183.562 129.989 183.355C129.278 183.17 128.747 182.549 128.487 181.866C128.227 181.182 128.198 180.436 128.188 179.705C128.164 177.995 128.232 176.283 128.437 174.609C130.247 174.978 132.081 175.517 134.094 176.295L134.094 176.295Z"
                      fill="#E3E3FF"
                      stroke="#010101"
                      strokeWidth="0.62243"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M131.276 175.375C131.057 176.838 130.875 178.023 130.729 179.495"
                      stroke="#010101"
                      strokeWidth="0.62243"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M44.6969 56.8395C44.8043 55.4791 44.3149 54.135 44.3259 52.7705C44.3293 52.3328 44.4196 51.8375 44.7891 51.6007C44.8676 51.5505 44.9587 51.5143 45.052 51.5147C45.1871 51.5154 45.3104 51.5913 45.4136 51.6781C45.9155 52.1003 46.093 52.7822 46.2437 53.4187C46.3742 53.9704 46.5051 54.5424 46.3943 55.0982C46.2228 55.9579 45.5111 56.6035 44.6969 56.8395Z"
                      fill="white"
                      stroke="#010101"
                      strokeWidth="0.62243"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M46.549 68.2226C45.4874 65.2984 44.4119 62.0105 44.4642 58.9016C44.5166 55.7927 45.9423 52.5093 48.7357 51.1172C48.9137 51.0285 49.1167 50.9465 49.3063 51.0062C49.7745 51.1538 49.6209 51.8519 49.3617 52.2673C48.6629 53.3883 47.8685 54.4504 46.9902 55.4385C47.5388 54.3043 48.3674 53.3056 49.3826 52.5542C49.5749 52.4119 50.0795 51.989 50.3185 52.0079C50.6991 52.0377 50.9381 52.4753 50.8926 52.8529C50.847 53.2305 50.3193 53.8338 50.0803 54.1306C49.3466 55.0417 48.573 55.9206 47.7619 56.7641C48.5161 55.9542 49.2703 55.1447 50.0245 54.3349C50.2233 54.1215 50.4688 53.8897 50.759 53.9233C51.311 53.9867 51.2924 54.8023 51.0618 55.3056C50.4824 56.5689 49.5795 57.6835 48.4622 58.5153C49.0271 57.9769 49.592 57.4385 50.1569 56.8997C50.3792 56.6879 50.6137 56.4685 50.9092 56.382C51.2048 56.2956 51.5781 56.3896 51.7048 56.669C51.8509 56.9907 51.5565 57.309 51.3949 57.6231C50.8865 58.6116 50.7275 58.7471 50.1004 59.7892C50.5075 60.8585 51.0401 62.9714 50.8554 63.1553C50.5253 63.4838 47.8503 66.6787 46.5482 68.223L46.549 68.2226Z"
                      fill="white"
                      stroke="#010101"
                      strokeWidth="0.62243"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M160.398 31.9152C158.954 31.6422 157.627 31.6071 156.265 31.6717C156 31.6841 155.748 31.8854 155.642 32.1281C155.423 32.6326 156.033 33.0883 156.548 33.285C157.888 33.7966 159.272 33.8593 160.698 34.0167C159.816 34.0368 158.98 33.939 158.111 33.7845C156.68 33.5304 155.105 33.7358 155.158 34.6382C155.195 35.2669 155.869 35.6059 156.488 35.7347C158.182 36.0873 159.571 35.9963 161.301 36.05C160.338 36.05 159.427 36.1572 158.464 36.1572C157.869 36.1572 156.838 36.4506 156.663 37.0165C156.541 37.4137 156.712 37.9416 157.072 38.1511C157.433 38.3607 157.865 38.3988 158.282 38.4324C159.384 38.5215 160.925 38.4317 162.027 38.5208C161.221 38.7704 157.12 37.8653 158.173 40.184C158.602 41.1294 160.812 40.881 162.543 40.9939C163.196 41.0365 163.989 41.009 164.316 40.4445C164.841 39.5368 163.813 36.4287 163.289 34.0855C163.067 33.097 162.722 31.3515 162.286 30.4363C162.079 30.002 161.764 29.5403 161.284 29.4836C160.821 29.4289 160.394 29.8008 160.229 30.2342C160.063 30.6677 160.288 30.8644 160.398 31.9152Z"
                      fill="white"
                      stroke="#010101"
                      strokeWidth="0.62243"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M23.2856 76.2712L25.1905 76.5873"
                      stroke="#010101"
                      strokeWidth="0.62243"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M15.9813 147.463L15.3462 150.096"
                      stroke="#010101"
                      strokeWidth="0.62243"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M42.8452 106.613C42.514 106.717 42.3493 107.153 42.5098 107.459C42.6703 107.765 43.0975 107.887 43.4082 107.733C43.7162 107.58 43.8767 107.172 43.7295 106.862C43.5823 106.552 43.1771 106.509 42.8452 106.613Z"
                      fill="#010101"
                      stroke="#010101"
                      strokeWidth="0.62243"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M172.742 116.533C172.411 116.637 172.246 117.073 172.407 117.379C172.567 117.685 172.994 117.807 173.305 117.653C173.613 117.5 173.774 117.092 173.626 116.782C173.479 116.472 173.074 116.429 172.742 116.533Z"
                      fill="#010101"
                      stroke="#010101"
                      strokeWidth="0.62243"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M167.334 13.1963C167.003 13.2998 166.839 13.7359 166.999 14.0421C167.16 14.3483 167.587 14.4699 167.897 14.3158C168.205 14.1629 168.366 13.7548 168.219 13.4448C168.072 13.1348 167.666 13.0925 167.334 13.1963Z"
                      fill="#010101"
                      stroke="#010101"
                      strokeWidth="0.62243"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </g>
                  <path
                    d="M94.0809 32.4555C106.084 26.8737 120.34 32.0792 125.921 44.0824C131.503 56.0856 126.298 70.3411 114.294 75.9229C102.291 81.5047 88.0358 76.2992 82.4539 64.296C82.0246 63.3726 82.425 62.2761 83.3483 61.8467C84.2716 61.4173 85.3682 61.8178 85.7976 62.7411C90.5207 72.8976 102.583 77.3023 112.74 72.5793C122.896 67.8562 127.301 55.7939 122.578 45.6373C117.855 35.4808 105.792 31.0761 95.6358 35.7991C94.7124 36.2285 93.6159 35.8281 93.1865 34.9048C92.7571 33.9814 93.1576 32.8849 94.0809 32.4555Z"
                    fill="#323232"
                  />
                  <path
                    d="M97.1127 41.89C98.0361 41.4607 99.1326 41.8611 99.562 42.7844L103.968 52.2581L111.212 48.8892C112.058 48.4956 113.05 48.7992 113.541 49.5638L113.661 49.7835C114.091 50.7069 113.69 51.8034 112.767 52.2328L103.851 56.3792C102.927 56.8085 101.831 56.4081 101.401 55.4848L96.2184 44.3393C95.789 43.416 96.1894 42.3194 97.1127 41.89Z"
                    fill="#010506"
                  />
                  <path
                    d="M80.2695 51.7191C80.723 50.4366 82.1299 49.7642 83.4128 50.2168C84.6956 50.6695 85.3689 52.076 84.917 53.3591C84.4651 54.6422 83.059 55.3164 81.7756 54.8653C80.591 54.4489 79.9247 53.2191 80.1834 52.0227L80.2695 51.7191Z"
                    fill="#010506"
                  />
                  <path
                    d="M84.1765 41.0634C84.655 39.7983 86.0681 39.1603 87.3335 39.638C88.5015 40.079 89.1358 41.3167 88.8513 42.5L88.76 42.7963C88.2786 44.0644 86.8647 44.7006 85.5999 44.2213C84.3351 43.742 83.698 42.3285 84.1765 41.0634Z"
                    fill="#323232"
                  />
                </svg>
              </div>
            </div>
          </div>

          {/* Text block */}
          <div
            className="w-full flex flex-col items-center gap-2"
            style={{ letterSpacing: '-0.03em' }}
          >
            <h1 className="text-[16px] leading-[1.22] font-medium text-center text-[#010506]">
              Session Expired
            </h1>
            <p className="text-[14px] leading-[1.4] text-center text-[rgba(1,5,6,0.6)]">
              Your session has expired due to inactivity. Please login again to
              continue.
            </p>
          </div>

          {/* Actions */}
          <div className="w-full flex items-center justify-center">
            <a
              href={redirectHref}
              className="h-10 px-4 inline-flex items-center justify-center rounded-full border border-[rgba(1,5,6,0.1)] text-[#010506] text-[16px] font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-black/30"
              aria-label="Sign In Again"
            >
              Sign In Again
            </a>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AuthFailed;
